import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_WINDOWS_FONT_FAMILY,
  WindowsFont,
  getGenericFontFromWindowsFont,
  getWindowsFontFromGenericFont,
} from 'common';
import {
  CapStyle,
  Color,
  DocumentAnnotation,
  EllipseAnnotation,
  EndCapStyle,
  Font,
  ImageAnnotation,
  LineAnnotation,
  LineAppearance,
  LineStyle,
  PagesOfDocumentAnnotations,
  PathAnnotation,
  RectangleAnnotation,
  Size,
  StampAnnotation,
  TextAnnotation,
  createArrowCap,
} from 'models';

export interface LayerDataCollection {
  LayerData: LayerData;
}

/**
 * The sparse object of Atalasoft annotation objects.
 */
export interface LayerDataItems {
  CustomRectangleData?: CustomRectangleData | CustomRectangleData[];
  CustomSignatureData?: EmbeddedImageData | EmbeddedImageData[];
  CustomStampData?: CustomStampData | CustomStampData[];
  CustomTextData?: CustomTextData | CustomTextData[];
  EllipseData?: EllipseData | EllipseData[];
  EmbeddedImageData?: EmbeddedImageData | EmbeddedImageData[];
  FreehandData?: FreehandData | FreehandData[];
  LineData?: LineData | LineData[];
  LinesData?: LinesData | LinesData[];
  RectangleData?: RectangleData | RectangleData[];
}

/**
 * Base LayerData inteface.
 *
 * These properties exist on all layer data objects.
 */
export interface AnnotationBase {
  CanMirror: boolean;
  CanMove: boolean;
  CanResize: boolean;
  CanRotate: boolean;
  CanSelect: boolean;
  CreationTime: string;
  ExtraProperties: ExtraProperties;
  Location: Point;
  ModifiedTime: string;
  Rotation: number;
  Size: Size;
  Visible: boolean;
}

/**
 * Layer root.
 */
export interface LayerData extends AnnotationBase {
  GroupAnnotation: boolean;
  Items: {
    Items: LayerDataItems;
  };
}

export interface Fill {
  Color: number;
  // Used by Legacy Desktop Stamps.
  HatchForeColor?: number;
  ctor: number;
}

export interface LocalFont {
  Bold: boolean;
  CharSet: number;
  Italic: boolean;
  Name: string;
  Size: number;
  Strikeout: boolean;
  Underline: boolean;
}

export interface ExtraProperties {
  ExtraPropertiesEntry: Array<boolean | string>;
}

export interface Image {
  BitCount: number;
  DpiX: number;
  DpiY: number;
  Height: number;
  ImageData: string;
  Width: number;
}

/**
 * Points.
 *
 * @todo I would prefer this could be deserialized without this extra nesting level.
 */
export interface Points {
  Points: Point | Point[] | number[];
}

export interface Cap {
  Size: Size;
  Style: EndCapStyle;
}

export interface Outline {
  Alignment: Alignment;
  Brush?: Fill;
  Color: number;
  DashCap: DashCap;
  DashOffset: number;
  DashStyle: DashStyle;
  EndCap: Cap;
  LineJoin: DashCap;
  MiterLimit: number;
  StartCap: Cap;
  Width: number;
  ctor: number;
}

export interface Shadow {
  Color: number;
  ctor: number;
}

export interface Point {
  x: number;
  y: number;
}

/// Specific annotation data types here:

export interface RectangleData extends AnnotationBase {
  Fill: Fill;
  Outline: Outline;
  Shadow: Shadow;
  ShadowOffset: Point;
  Translucent: boolean;
}

/**
 * Convert from Atalasoft `RectangleData` to a `RectangleAnnotation`.
 *
 * @param rectangleData `RectangleData` item.
 * @param pageIndex Page index.
 * @returns A new `RectangleAnnotation`.
 */
export const ConvertRectangleDataToRectangleAnnotation = (
  rectangleData: RectangleData,
  pageIndex: number
): RectangleAnnotation => {
  return new RectangleAnnotation(
    pageIndex,
    rectangleData.Location.x,
    rectangleData.Location.y,
    rectangleData.Size.height,
    rectangleData.Size.width,
    {
      caps: CapStyle.Butt,
      color: Color.convertColor(rectangleData.Outline.Color),
      style: LineStyle.Solid,
      thickness: rectangleData.Outline.Width,
    },
    Color.convertColor(rectangleData.Fill.Color),
    rectangleData.Rotation,
    true
  );
};

export interface CustomRectangleData extends AnnotationBase {
  Fill: Fill;
  Tag: string;
  Translucent: boolean;
}

/**
 * Convert from Atalasoft `CustomRectangleData` to a `RectangleAnnotation`.
 *
 * @param customRectangleData `CustomRectangleData` item.
 * @param pageIndex Page index.
 * @returns A new `RectangleAnnotation`.
 */
export const ConvertCustomRectangleDataToRectangleAnnotation = (
  customRectangleData: CustomRectangleData,
  pageIndex: number
): RectangleAnnotation => {
  // Special handlers:
  if (customRectangleData.Tag === 'Highlight') {
    // If highlight created in Legacy Desktop client, replace the fill color
    // with correct transparent yellow fill.
    customRectangleData.Fill.Color = -2130706688;
  }

  return new RectangleAnnotation(
    pageIndex,
    customRectangleData.Location.x,
    customRectangleData.Location.y,
    customRectangleData.Size.height,
    customRectangleData.Size.width,
    {
      // CustomRectangleData is only a fill rectangle, no border data.
      caps: CapStyle.Butt,
      color: new Color(0, 0, 0),
      style: LineStyle.Solid,
      thickness: 0,
    },
    Color.convertColor(customRectangleData.Fill.Color),
    customRectangleData.Rotation,
    true,
    '',
    true
  );
};

export interface CustomStampData extends AnnotationBase {
  CornerRadius: number;
  Fill: Fill;
  Font: LocalFont;
  FontBrush: Fill;
  Outline: Outline;
  Padding: number;
  Tag: string;
  Text: string;
}

/**
 * Square 9 API WebAnnotation data.
 *
 * This is the construct used by the Square 9 Viewer and Update API endpoints for "Web Annotations".
 */

/**
 * Defines the fill properties for a web annotation.
 */
interface WebAnnotationFill {
  color: string;
  opacity: number;
}

/**
 * Extends fill properties with outline specifics for a web annotation.
 */
export interface WebAnnotationOutline extends WebAnnotationFill {
  width: number;
  startcap: WebAnnotationLineCap;
  endcap: WebAnnotationLineCap;
}

export enum WebAnnotationLineWidth {
  Narrow = 10,
  Medium = 15,
  Wide = 25,
}

export enum WebAnnotationLineHeight {
  Short = 10,
  Medium = 15,
  Long = 25,
}

/**
 * Represents the line cap properties for a web annotation.
 */
export interface WebAnnotationLineCap {
  style: string;
  width: WebAnnotationLineWidth;
  height: WebAnnotationLineHeight;
}

/**
 * Defines text styling for a web annotation.
 */
export interface WebAnnotationFont {
  bold: boolean;
  italic: boolean;
  strike: boolean;
  underline: boolean;
  color: string;
  family: WindowsFont;
  size: number;
}

/**
 * Specifies text configuration options for a web annotation.
 */
export interface WebAnnotationTextConfig {
  value: string;
  align: string;
  font: WebAnnotationFont;
  readonly: boolean;
  autoscale: boolean;
}

/**
 * Holds additional data for a web annotation.
 */
export interface WebAnnotationExtra {
  S9Type: string;
}

/**
 * Represents the complete data structure for a web annotation.
 */
export interface WebAnnotationData {
  type: string;
  rotation: number;
  burn: boolean;
  extra: WebAnnotationExtra;
  fill: WebAnnotationFill;
  height: number;
  movable: boolean;
  outline?: WebAnnotationOutline;
  points: Point[];
  readonly: boolean;
  resizable: boolean;
  rotatable: boolean;
  cornerradius: number;
  selectable: boolean;
  selected: boolean;
  src: string;
  text: WebAnnotationTextConfig;
  tooltip: string;
  username: string;
  visible: boolean;
  width: number;
  x: number;
  y: number;
}

/** Describes a dictionary of document annotations, keyed by page number. */
export declare type PagesOfWebAnnotations = Map<number, WebAnnotationData[]>;

/**
 * Creates annotation data structure required for submissions to the Square 9 API.
 *
 * Essentially reverses the conversion process from the Square 9 API to the
 * internal models. However, the original data came from the Atalasoft based XML object model,
 * these are cast to our "WebAnnotationData" type that the API has traditionally used in GSW.
 */
export const createWebAnnotationData = (
  documentAnnotations: PagesOfDocumentAnnotations
): PagesOfWebAnnotations => {
  // Create a new map to store the web annotation data.
  const webAnnotationDataDict: PagesOfWebAnnotations = new Map();
  // Iterate over each page and convert annotations.
  documentAnnotations.forEach((pageAnnotations, pageIndex) => {
    const convertedAnnotations = pageAnnotations.map(
      (annotation: DocumentAnnotation) => {
        // Create a base annotation object.
        const baseAnnotation: Partial<WebAnnotationData> = {
          type: getAnnotationType(annotation),
          rotation: 0,
          burn: annotation.burn,
          extra: { S9Type: getAnnotationType(annotation) },
          fill: {
            color: '#000000',
            opacity: 1,
          },
          height: 0,
          movable: true,
          outline: {
            color: '#000000',
            opacity: 1,
            width: 1,
            startcap: {
              style: 'none',
              width: WebAnnotationLineWidth.Narrow,
              height: WebAnnotationLineHeight.Short,
            },
            endcap: {
              style: 'none',
              width: WebAnnotationLineWidth.Narrow,
              height: WebAnnotationLineHeight.Short,
            },
          },
          points: [],
          readonly: false,
          resizable: true,
          rotatable: true,
          cornerradius: 0,
          selectable: true,
          selected: false,
          src: '',
          text: {
            value: '',
            align: 'center',
            font: {
              bold: false,
              italic: false,
              strike: false,
              underline: false,
              color: '#000000',
              family: DEFAULT_WINDOWS_FONT_FAMILY,
              size: 12,
            },
            readonly: false,
            autoscale: false,
          },
          tooltip: '',
          username: '',
          visible: true,
          width: 0,
          x: 0,
          y: 0,
        };

        // Handle rectangle annotations
        if (annotation instanceof RectangleAnnotation) {
          const rectangle = {
            ...baseAnnotation,
            fill: {
              ...baseAnnotation.fill!,
              color: annotation.color.hexCode,
              opacity: annotation.color.opacity,
            },
            outline: {
              color: annotation.border.color.hexCode,
              opacity: annotation.border.color.opacity,
              width: annotation.border.thickness,
              startcap: {
                style: 'none',
                width: WebAnnotationLineWidth.Narrow,
                height: WebAnnotationLineHeight.Short,
              },
              endcap: {
                style: 'none',
                width: WebAnnotationLineWidth.Narrow,
                height: WebAnnotationLineHeight.Short,
              },
            },
            rotation: annotation.rotation,
            width: annotation.width,
            height: annotation.height,
            x: annotation.x,
            y: annotation.y,
            src: '', // Rectangle annotations don't have a direct src property
          } as WebAnnotationData;

          if (annotation.isHighlight) {
            rectangle.type = 'highlight';
            delete rectangle.outline;
          }
          return rectangle;
        }

        // Handle stamp annotations
        if (annotation instanceof StampAnnotation) {
          return {
            ...baseAnnotation,
            width: annotation.width,
            height: annotation.height,
            rotation: annotation.rotation,
            cornerradius: annotation.cornerRadius,
            text: {
              value: annotation.text,
              align: 'center',
              font: {
                bold: false,
                italic: false,
                strike: false,
                underline: false,
                color: annotation.color.hexCode,
                family: getWindowsFontFromGenericFont(annotation.font),
                size: annotation.fontSize,
              },
            },
            fill: {
              color: '',
              opacity: 0,
            },
            x: annotation.x,
            y: annotation.y,
            outline: {
              startcap: {
                style: 'none',
                width: WebAnnotationLineWidth.Narrow,
                height: WebAnnotationLineHeight.Short,
              },
              endcap: {
                style: 'none',
                width: WebAnnotationLineWidth.Narrow,
                height: WebAnnotationLineHeight.Short,
              },
              color: annotation.border.color.hexCode,
              opacity: annotation.border.color.opacity,
              width: annotation.border.thickness,
            },
            src: '', // Stamp annotations don't have a direct src property
          } as WebAnnotationData;
        }

        // Handle text annotations
        if (annotation instanceof TextAnnotation) {
          return {
            ...baseAnnotation,
            text: {
              value: annotation.text || '',
              font: {
                ...baseAnnotation.text!.font,
                bold: annotation.font.bold,
                italic: annotation.font.italic,
                strike: annotation.font.strikeout,
                underline: annotation.font.underline,
                size: annotation.font.size || 12,
                color: annotation.color.hexCode,
                family: getWindowsFontFromGenericFont(annotation.font.name),
              },
              align: annotation.alignment,
            },
            outline: {
              startcap: {
                style: 'none',
                width: WebAnnotationLineWidth.Narrow,
                height: WebAnnotationLineHeight.Short,
              },
              endcap: {
                style: 'none',
                width: WebAnnotationLineWidth.Narrow,
                height: WebAnnotationLineHeight.Short,
              },
              color: annotation.border.color.hexCode,
              opacity: annotation.border.color.opacity,
              width: annotation.border.thickness,
            },
            fill: {
              color: annotation.fillColor.hexCode,
              opacity: annotation.fillColor.opacity,
            },
            width: annotation.width,
            height: annotation.height,
            x: annotation.x,
            y: annotation.y,
            rotation: annotation.rotation,
          } as WebAnnotationData;
        }

        // Handle line annotations
        if (annotation instanceof LineAnnotation) {
          const points: { x: number; y: number }[] = [];
          points.push({ x: annotation.x, y: annotation.y });
          points.push({ x: annotation.end.x, y: annotation.end.y });
          return {
            ...baseAnnotation,
            rotation: annotation.rotation,
            points,
            outline: {
              ...baseAnnotation.outline!,
              color: annotation.line.color.hexCode,
              opacity: annotation.line.color.opacity,
              width: annotation.line.thickness || 1,
              startcap: {
                style: annotation.startcap.style || 'none',
                width: annotation.startcap.size.width,
                height: annotation.startcap.size.height,
              },
              endcap: {
                style: annotation.endcap.style || 'none',
                width: annotation.endcap.size.width,
                height: annotation.endcap.size.height,
              },
            },
          } as WebAnnotationData;
        }

        // Handle path annotations
        if (annotation instanceof PathAnnotation) {
          const pathPoints: { x: number; y: number }[] = [];
          for (let i = 0, c = annotation.pathPoints.length; i < c; i = i + 2) {
            /**
             * When saving we need to put the points back to where they belong on the page
             * and not in relation to x and y.
             */
            pathPoints.push({
              x: annotation.pathPoints[i] + annotation.x,
              y: annotation.pathPoints[i + 1] + annotation.y,
            });
          }

          /** To png doesn't like negatives or 0, and the save doesn't use height or width so just getting the absolute value. */
          annotation.height = Math.abs(annotation.height);
          annotation.width = Math.abs(annotation.width);

          // Since pathPoints is private, we need to use the toPng method
          // to get a data URL for the path
          return {
            ...baseAnnotation,
            type: 'freehand',
            x: annotation.x,
            y: annotation.y,
            height: annotation.height,
            width: annotation.width,
            points: pathPoints,
            rotation: annotation.rotation,
            src: annotation.toPng(),
            outline: {
              color: annotation.color.hexCode,
              opacity: annotation.color.opacity,
              width: annotation.thickness || 1,
            },
          } as WebAnnotationData;
        }

        // Handle image annotations
        if (annotation instanceof ImageAnnotation) {
          const urlParams = new URLSearchParams(
            new URL(annotation.image).search
          );
          return {
            ...baseAnnotation,
            src: urlParams.get('cachedFile') || annotation.image,
            width: annotation.width,
            height: annotation.height,
            rotation: annotation.rotation,
            x: annotation.x,
            y: annotation.y,
          } as WebAnnotationData;
        }

        // Handle ellipse annotations
        if (annotation instanceof EllipseAnnotation) {
          return {
            ...baseAnnotation,
            x: annotation.x,
            y: annotation.y,
            rotation: annotation.rotation,
            fill: {
              color: annotation.color.hexCode,
              opacity: annotation.color.opacity,
            },
            width: annotation.width,
            height: annotation.height,
            outline: {
              ...baseAnnotation.outline!,
              color: annotation.border.color.hexCode,
              opacity: annotation.border.color.opacity,
              width: annotation.border.thickness || 1,
            },
          } as WebAnnotationData;
        }

        // Default case - return base annotation
        return baseAnnotation as WebAnnotationData;
      }
    );
    webAnnotationDataDict.set(pageIndex, convertedAnnotations);
  });

  return webAnnotationDataDict;
};

function getAnnotationType(annotation: DocumentAnnotation): string {
  if (annotation instanceof StampAnnotation) return 'stamp';
  if (annotation instanceof TextAnnotation) return 'text';
  if (annotation instanceof RectangleAnnotation) return 'rectangle';
  if (annotation instanceof LineAnnotation) return 'line';
  if (annotation instanceof ImageAnnotation) return 'image';
  if (annotation instanceof EllipseAnnotation) return 'ellipse';
  if (annotation instanceof PathAnnotation) return 'freehand';
  return 'unknown';
}

/**
 * Convert from Atalasoft `CustomStampData` to a `StampAnnotation`.
 *
 * @param stampData `CustomStampData` item.
 * @param pageIndex Page index.
 * @returns A new `StampAnnotation`.
 */
export const ConvertCustomStampDataToStampAnnotation = (
  stampData: CustomStampData,
  pageIndex: number
): StampAnnotation => {
  const stampAnnotation = new StampAnnotation(
    pageIndex,
    stampData.Location.x,
    stampData.Location.y,
    stampData.Size.width,
    stampData.Size.height,
    stampData.Font.Size,
    stampData.Text,
    '',
    // Include fallover for missing color property from Legacy Desktop annotations.
    Color.convertColor(
      stampData.FontBrush.Color ?? stampData.FontBrush.HatchForeColor
    ),
    undefined,
    stampData.Rotation,
    stampData.Visible,
    new LineAppearance(
      undefined,
      Color.convertColor(stampData.Outline.Color),
      undefined,
      stampData.Outline.Width
    ),
    stampData.CornerRadius
  );
  stampAnnotation.border.thickness = stampData.Outline.Width;
  // Match Windows font to generic font.
  stampAnnotation.font = getGenericFontFromWindowsFont(
    stampData.Font.Name as WindowsFont
  );
  return stampAnnotation;
};

export interface CustomTextData extends AnnotationBase {
  Alignment: Alignment;
  AutoSize: boolean;
  Fill: Fill;
  Font: LocalFont;
  FontBrush: Fill;
  FormatFlags: number;
  LineAlignment: Alignment;
  Minimized: boolean;
  OCRType: number;
  Outline: Outline;
  Padding: number;
  RenderingHint: string;
  Shadow: Fill;
  ShadowMode: string;
  ShadowOffset: Point;
  Tag: string;
  Text: string | number;
  Trimming: Style;
}

/**
 * Convert from Atalasoft `CustomTextData` to a `TextAnnotation`.
 *
 * @param customTextData `CustomTextData` item.
 * @param pageIndex Page index.
 * @returns A new `TextAnnotation`.
 */
export const ConvertTextDataToTextAnnotation = (
  customTextData: CustomTextData,
  pageIndex: number
): TextAnnotation => {
  const textAnnotation = new TextAnnotation(
    pageIndex,
    customTextData.Location.x,
    customTextData.Location.y,
    customTextData.Size.width,
    customTextData.Size.height,
    customTextData.Font.Size,
    customTextData.Alignment,
    customTextData.Text.toString(),
    Color.convertColor(customTextData.FontBrush.Color),
    '',
    new Font(
      DEFAULT_FONT_FAMILY,
      customTextData.Font.Bold,
      customTextData.Font.Italic,
      customTextData.Font.Size,
      customTextData.Font.Strikeout,
      customTextData.Font.Underline
    ), // Mapped to matching standard font bellow.
    {
      caps: CapStyle.Butt,
      color: Color.convertColor(customTextData.Outline.Color),
      style: LineStyle.Solid,
      thickness: customTextData.Outline.Width,
    },
    Color.convertColor(customTextData.Fill.Color),
    customTextData.Rotation,
    customTextData.Visible
  );
  // Match Windows font to generic font.
  textAnnotation.font.name = getGenericFontFromWindowsFont(
    customTextData.Font.Name as WindowsFont
  );
  return textAnnotation;
};

export interface EllipseData extends AnnotationBase {
  Fill: Fill;
  Outline: Outline;
  Shadow: Shadow;
  ShadowOffset: Point;
  Translucent: boolean;
}

/**
 * Convert from Atalasoft `EllipseData` to a `EllipseAnnotation`.
 *
 * @param ellipseData `EllipseData` item.
 * @param pageIndex Page index.
 * @returns A new `EllipseAnnotation`.
 */
export const ConvertEllipseDataToEllipseAnnotation = (
  ellipseData: EllipseData,
  pageIndex: number
): EllipseAnnotation => {
  return new EllipseAnnotation(
    pageIndex,
    // Atalasoft uses top-right, pdf uses center, adjust here.
    // Convert both starting location and size.
    ellipseData.Location.x + ellipseData.Size.width / 2,
    ellipseData.Location.y + ellipseData.Size.height / 2,
    ellipseData.Size.width / 2,
    ellipseData.Size.height / 2,
    {
      color: Color.convertColor(ellipseData.Outline.Color),
      caps: CapStyle.Butt,
      thickness: ellipseData.Outline.Width,
      style: LineStyle.Solid,
    },
    Color.convertColor(ellipseData.Fill.Color),
    ellipseData.Rotation,
    ellipseData.Visible,
    '',
    ellipseData.Size.height,
    ellipseData.Size.width
  );
};

export interface EmbeddedImageData extends AnnotationBase {
  Image: Image;
  ShadowOffset: Point;
}

/**
 * Convert from Atalasoft `EmbeddedImageData` to a `ImageAnnotation`.
 *
 * @param embeddedImageData `EmbeddedImageData` item.
 * @param pageIndex Page index.
 * @returns A new `ImageAnnotation`.
 */
export const ConvertEmbeddedImageDataToImageAnnotation = (
  embeddedImageData: EmbeddedImageData,
  pageIndex: number
): ImageAnnotation => {
  // Configure an empty Image annotation.
  const annotation = new ImageAnnotation(
    pageIndex,
    embeddedImageData.Location.x,
    embeddedImageData.Location.y,
    embeddedImageData.Size.height,
    embeddedImageData.Size.width,
    '', // Value will be set below.
    embeddedImageData.Image.Height,
    embeddedImageData.Image.Width,
    embeddedImageData.Rotation,
    undefined,
    embeddedImageData.Visible
  );
  // Convert the AtalaImage and set the image to the PNG result.
  annotation.createPngFromAtalaImage(embeddedImageData.Image.ImageData);
  return annotation;
};

export interface FreehandData extends AnnotationBase {
  ClosedShape: boolean;
  Fill: Fill;
  LineType: LineType;
  // This can denote if it is a "Signature", only seems used then.
  Name?: string;
  Outline: Outline;
  Points: Points;
  Rotation: number;
  Size: Size;
  Translucent: boolean;
  // Non-signature, freehand drawings have this property.
  UserName?: string;
  Visible: boolean;
}

/**
 * Convert from Atalasoft `FreehandData` to a `PathAnnotation`.
 *
 * @param freehandData `FreehandData` item.
 * @param pageIndex Page index.
 * @returns A new `PathAnnotation`.
 */
export const ConvertFreehandDataToPathAnnotation = (
  freehandData: FreehandData,
  pageIndex: number
): PathAnnotation => {
  return new PathAnnotation(
    pageIndex,
    freehandData.Location.x,
    freehandData.Location.y,
    Color.convertColor(freehandData.Outline.Color),
    freehandData.Outline.Width,
    freehandData.Rotation,
    freehandData.Visible,
    freehandData.Points.Points as number[],
    freehandData.Size.height,
    freehandData.Size.width
  );
};

export interface LineData extends AnnotationBase {
  EndPoint: Point;
  Outline: Outline;
  StartPoint: Point;
  Translucent: boolean;
}

/**
 * Convert from Atalasoft `LineData` to a `LineAnnotation`.
 *
 * @param lineData `LineData` item.
 * @param pageIndex Page index.
 * @returns A new `LineAnnotation`.
 */
export const ConvertLineDataToLineAnnotation = (
  lineData: LineData,
  pageIndex: number
): LineAnnotation => {
  return new LineAnnotation(
    pageIndex,
    lineData.Location.x + lineData.StartPoint.x,
    lineData.Location.y + lineData.StartPoint.y,
    // Atalasoft stores end points relative to start.
    {
      x: lineData.Location.x + lineData.EndPoint.x,
      y: lineData.Location.y + lineData.EndPoint.y,
    },
    new LineAppearance(
      CapStyle.Butt,
      Color.convertColor(lineData.Outline.Color),
      LineStyle.Solid,
      lineData.Outline.Width,
      // Support arrows by setting the end cap styling.
      lineData.Outline.EndCap.Style === EndCapStyle.FilledArrow
        ? EndCapStyle.Arrow
        : EndCapStyle.None
    ),
    lineData.Size.width,
    lineData.Visible,
    '',
    lineData.Rotation,
    0,
    0,
    createArrowCap(
      {
        height: lineData.Outline.StartCap.Size.height,
        width: lineData.Outline.StartCap.Size.width,
      },
      lineData.Outline.StartCap.Style
    ),
    createArrowCap(
      {
        height: lineData.Outline.EndCap.Size.height,
        width: lineData.Outline.EndCap.Size.width,
      },
      lineData.Outline.EndCap.Style
    )
  );
};

export interface LinesData extends AnnotationBase {
  Outline: Outline;
  Points: Points;
  Translucent: boolean;
}

/**
 * Convert from Atalasoft `LinesData` to a `PathAnnotation`.
 *
 * @param linesData `LinesData` item.
 * @param pageIndex Page index.
 * @returns A new `PathAnnotation`.
 */
export const ConvertLinesDataToPathAnnotation = (
  linesData: LinesData,
  pageIndex: number
): PathAnnotation => {
  return new PathAnnotation(
    pageIndex,
    linesData.Location.x,
    linesData.Location.y,
    Color.convertColor(linesData.Outline.Color),
    linesData.Outline.Width,
    linesData.Rotation,
    linesData.Visible,
    linesData.Points.Points as number[],
    linesData.Size.height,
    linesData.Size.width
  );
};

/// Layer data object enums:
// TODO: These enums are not complete.

export enum Alignment {
  Center = 'Center',
  Near = 'Near',
  Far = 'Far',
}

export enum DashCap {
  Round = 'Round',
}

export enum DashStyle {
  Solid = 'Solid',
}

export enum Style {
  None = 'None',
}

export enum LineType {
  Straight = 'Straight',
}
